import { useCallback, useEffect, useRef, useState } from "react";
import { getLeadInteractions, type TLeadInteraction, type TSmsStatus } from "@/services/interactions.service";
import { useSmsRefreshStore } from "@/store/sms/sms-refresh.store";

export type SmsMessage = {
  id: number | string;
  body: string;
  date: string;
  direction: "inbound" | "outbound";
  fromNumber?: string;
  /** Where the text stands (sent ✓, delivered ✓✓, undelivered, unconfirmed); absent on imports. */
  status?: TSmsStatus;
  /** The provider's reason when `undelivered`. */
  errorCode?: number;
  /**
   * Who sent an outbound text — the history is shared (teammates' lines and the
   * company line mix on it), so a bubble names its real sender, never just "you".
   * Null = a workflow or an import with no user behind it.
   */
  rep?: {userId: number | null; name: string | null; avatar: string | null} | null;
  /** HubSpot SMS: the actual sender (person or automation); the rep there is just the owner. */
  sentBy?: string;
};

/** How many of the latest texts the composer shows above the input. */
const THREAD_SIZE = 20;

export const toMessage = (i: TLeadInteraction): SmsMessage => ({
  id: i.id,
  body: i.sms?.body ?? "",
  date: i.date,
  direction: i.direction ?? "outbound",
  fromNumber: i.sms?.fromNumber,
  status: i.sms?.status,
  errorCode: i.sms?.errorCode,
  rep: i.rep ?? null,
  sentBy: i.sms?.sentBy,
});

/**
 * The lead's recent texts, oldest first (GET /lead/{id}/interactions?type=sms).
 * `append` shows a just-sent text right away; `reload` swaps in the server's
 * copy (which carries the sender number) without flashing the loader.
 */
export function useSmsThread(leadId: number) {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Only the latest request may write: a slow first load must not overwrite
  // the reload that followed a send.
  const requestRef = useRef(0);

  const load = useCallback(
    async (silent = false) => {
      const request = ++requestRef.current;
      if (!silent) setLoading(true);
      try {
        const { data } = await getLeadInteractions(leadId, 1, THREAD_SIZE, "sms");
        if (request !== requestRef.current) return;
        setMessages(data.map(toMessage).reverse());
        setError(false);
      } catch {
        if (request !== requestRef.current) return;
        // A silent refresh failing keeps what's on screen.
        if (!silent) setError(true);
      } finally {
        if (request === requestRef.current) setLoading(false);
      }
    },
    [leadId]
  );

  // A new lead's load bumps requestRef, so the previous lead's late answer is dropped.
  useEffect(() => {
    setMessages([]);
    load();
  }, [load]);

  // A messaging socket event (inbound text, delivery status) bumped this lead:
  // refresh silently, so an open thread updates in place. The ref skips the
  // mount (the effect above already loaded) and a lead switch (ditto).
  const version = useSmsRefreshStore((s) => s.versionByLead[leadId] ?? 0);
  const seenRef = useRef<{leadId: number; version: number} | null>(null);
  useEffect(() => {
    const seen = seenRef.current;
    seenRef.current = { leadId, version };
    if (seen && seen.leadId === leadId && version > seen.version) load(true);
  }, [leadId, version, load]);

  // A silent refresh can land the sent text before the POST answers; appending
  // it again would double the bubble (and its React key).
  const append = useCallback(
    (message: SmsMessage) =>
      setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message])),
    []
  );
  const reload = useCallback(() => load(true), [load]);

  return { messages, loading, error, append, reload };
}
